/**
 * mare-funcoes.js — Maré Chocolate
 * ─────────────────────────────────────────────
 * Cálculo de frete:
 *   1. Busca as tarifas reais do Frenet via endpoint do Shopify
 *      (/cart/shipping_rates.json) — valores idênticos ao checkout
 *   2. Se o Shopify não retornar tarifas, usa a tabela Correios 2024
 *      como fallback (com desconto do contrato configurável)
 *   - Salva CEP no localStorage e restaura após atualização do carrinho
 * ─────────────────────────────────────────────
 */

/* ─── Fallback: tabela Correios 2024 ────────────────────────────────────────
   Usada apenas se o Shopify/Frenet não retornar tarifas.
   DESCONTO_CONTRATO: percentual de desconto do contrato (ex: 0.15 = 15%)
─────────────────────────────────────────────────────────────────────────── */
const DESCONTO_CONTRATO = 0.00;

/* ─── Frete grátis por região ───────────────────────────────────────────────
   Sudeste: R$ 150 | Sul: R$ 250 | Resto: R$ 299
─────────────────────────────────────────────────────────────────────────── */
function _freteGratisMin(uf) {
  if (['RJ','SP','MG','ES'].includes(uf)) return 15000;
  if (['PR','SC','RS'].includes(uf))      return 25000;
  return 29900;
}

/* ─── Prazo regional dos Correios (origem: Rio de Janeiro) ──────────────────
   Dias úteis base de trânsito do Correios (PAC).
   O prazo exibido = valor abaixo + 2 dias de processamento.
─────────────────────────────────────────────────────────────────────────── */
const PRAZO_BASE_UF = {
  RJ: 4,
  SP: 5, MG: 5, ES: 5,
  PR: 7, SC: 7, RS: 8,
  GO: 7, DF: 7, MT: 9, MS: 8,
  BA: 8, SE: 9, AL: 9, PE: 9, PB: 10, RN: 10, CE: 10, PI: 11, MA: 11,
  TO: 10, PA: 11, AM: 12, AC: 13, RR: 13, RO: 11, AP: 12,
};

function _prazoRegional(uf) {
  return PRAZO_BASE_UF[(uf || '').toUpperCase()] ?? 12;
}

const EMBALAGEM = { comprimento: 22, largura: 13.7, altura: 4.2 };
const PESO_PADRAO_G = 100;

const TABELA = {
  PAC: [
    [   100, 15.90, 17.50, 18.20, 19.80 ],
    [   200, 16.80, 18.40, 19.10, 20.90 ],
    [   300, 17.50, 19.20, 20.00, 21.80 ],
    [   500, 18.90, 20.80, 21.60, 23.50 ],
    [  1000, 21.40, 23.50, 24.40, 26.60 ],
    [  2000, 25.80, 28.40, 29.50, 32.10 ],
    [  3000, 30.20, 33.20, 34.50, 37.60 ],
    [  5000, 39.00, 42.90, 44.60, 48.60 ],
    [ 10000, 55.50, 61.10, 63.40, 69.10 ],
    [ 15000, 72.00, 79.20, 82.30, 89.70 ],
    [ 20000, 88.50, 97.40, 101.1, 110.2 ],
    [ 30000, 121.5, 133.6, 138.8, 151.3 ],
  ],
  SEDEX: [
    [   100, 19.80, 22.50, 24.30, 27.10 ],
    [   200, 21.20, 24.10, 26.10, 29.10 ],
    [   300, 22.40, 25.50, 27.60, 30.80 ],
    [   500, 24.60, 28.00, 30.30, 33.80 ],
    [  1000, 29.00, 33.10, 35.80, 39.90 ],
    [  2000, 37.80, 43.10, 46.60, 52.00 ],
    [  3000, 46.60, 53.10, 57.40, 64.00 ],
    [  5000, 64.20, 73.20, 79.10, 88.20 ],
    [ 10000, 99.30, 113.2, 122.3, 136.5 ],
    [ 15000, 134.3, 153.2, 165.6, 184.7 ],
    [ 20000, 169.4, 193.1, 208.8, 232.9 ],
    [ 30000, 239.5, 273.1, 295.1, 329.2 ],
  ],
};

const PRAZOS = { PAC: 8, SEDEX: 3 };

const UF_REGIAO = {
  RJ: 1,
  SP: 2, MG: 2, ES: 2,
  PR: 3, SC: 3, RS: 3,
};

function getRegiao(uf) {
  return UF_REGIAO[(uf || '').toUpperCase()] || 4;
}

function pesoCubico(qtd) {
  const volCm3 = EMBALAGEM.comprimento * EMBALAGEM.largura * EMBALAGEM.altura * qtd;
  return (volCm3 / 6000) * 1000;
}

function calcularValorLocal(servico, pesoG, regiao) {
  const idx = regiao - 1;
  for (const faixa of TABELA[servico]) {
    if (pesoG <= faixa[0]) return faixa[idx + 1] * (1 - DESCONTO_CONTRATO);
  }
  return null;
}

/* ─── Cache ViaCEP por sessão ─── */
const _cepCache = {};

async function _buscarCep(cep) {
  if (_cepCache[cep]) return _cepCache[cep];
  const data = await fetch(`https://viacep.com.br/ws/${cep}/json/`).then(r => r.json());
  if (!data.erro) _cepCache[cep] = data;
  return data;
}

/* ─── Busca tarifas reais do Shopify/Frenet ─────────────────────────────────
   Retorna array de { name, price, prazoLabel } ou [] se não disponível
─────────────────────────────────────────────────────────────────────────── */
function _prazoFallback(nome) {
  const n = (nome || '').toLowerCase();
  if (n.includes('sedex 10') || n.includes('sedex 12')) return 1;
  if (n.includes('sedex'))  return 3;
  if (n.includes('pac'))    return 8;
  if (n.includes('mini'))   return 10;
  return null;
}

async function _buscarTarifasShopify(cep, uf, cidade, cartTotal) {
  const params = new URLSearchParams();
  params.set('shipping_address[zip]',      cep);
  params.set('shipping_address[country]',  'Brazil');
  params.set('shipping_address[province]', uf);
  params.set('shipping_address[city]',     cidade || '');

  const res = await fetch(`/cart/shipping_rates.json?${params.toString()}`);
  if (!res.ok) return [];

  const data  = await res.json();
  const rates = data.shipping_rates || [];

  const minGratis  = _freteGratisMin(uf);
  const freteGratis = cartTotal >= minGratis;
  const menorPreco  = Math.min(...rates.map(r => parseFloat(r.price)));

  return rates.map(r => {
    const preco     = parseFloat(r.price);
    const isCheapest = preco === menorPreco;

    let prazoLabel = '';
    let baseDias = null;

    if (r.delivery_range?.max != null) {
      const v = Number(r.delivery_range.max);
      if (!isNaN(v) && v > 0) baseDias = v;
    }
    if (baseDias === null && r.delivery_days != null) {
      const raw = Array.isArray(r.delivery_days) && r.delivery_days.length > 0
        ? r.delivery_days[r.delivery_days.length - 1]
        : r.delivery_days;
      const v = Number(raw);
      if (!isNaN(v) && v > 0) baseDias = v;
    }
    if (baseDias !== null) {
      prazoLabel = `até ${baseDias + 2} dias úteis`;
    } else {
      const dias = _prazoFallback(r.name) ?? _prazoRegional(uf);
      prazoLabel = `até ${dias + 2} dias úteis`;
    }

    return {
      nome:      r.name,
      price:     freteGratis && isCheapest ? 0 : preco,
      prazoLabel,
      fonte:     'shopify',
    };
  });
}

/* ─── Controle de foco no campo de CEP ─── */
let _cepEmFoco = false;
document.addEventListener('focusin',  (e) => { if (e.target.hasAttribute('data-frete-cep')) _cepEmFoco = true;  });
document.addEventListener('focusout', (e) => { if (e.target.hasAttribute('data-frete-cep')) _cepEmFoco = false; });

/* ─── Formatar CEP enquanto digita ─── */
document.addEventListener('input', (e) => {
  if (!e.target.closest('[data-frete-widget]') || !e.target.hasAttribute('data-frete-cep')) return;
  const v = e.target.value.replace(/\D/g, '');
  e.target.value = v.length > 5 ? v.slice(0, 5) + '-' + v.slice(5, 8) : v;
});

/* ─── Enter para calcular ─── */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' || !e.target.hasAttribute('data-frete-cep')) return;
  const btn = e.target.closest('[data-frete-widget]')?.querySelector('[data-frete-btn]');
  if (btn && !btn.disabled) btn.click();
});

/* ─── Renderiza as opções de frete ──────────────────────────────────────────*/
function _renderizarServicos(servicos, viaCep, fonte, widget) {
  if (!widget) widget = document.querySelector('[data-frete-widget]');
  const result = widget?.querySelector('[data-frete-result]');
  if (!result) return;

  const fonteLabel = fonte === 'shopify'
    ? '<span style="font-size:0.65rem;opacity:0.5;display:block;margin-bottom:4px">via Frenet/Shopify</span>'
    : '<span style="font-size:0.65rem;color:#c0541a;display:block;margin-bottom:4px">⚠ tabela local (Frenet não respondeu)</span>';

  result.innerHTML = `
    <p class="mare-frete-cidade">${viaCep.localidade} — ${viaCep.uf}</p>
    ${fonteLabel}
    ${servicos.map(s => {
      const valor  = s.price ?? s.valor ?? 0;
      const exibir = valor === 0 ? 'Grátis' : 'R$ ' + valor.toFixed(2).replace('.', ',');
      const prazo  = s.prazoLabel || (s.prazo ? `até ${s.prazo + 2} dias úteis` : '');
      return `
        <div class="mare-frete-opcao" onclick="mareSelecionarFrete(this)">
          <div>
            <p class="mare-frete-opcao__nome">${s.nome || s.name}</p>
            ${prazo ? `<p class="mare-frete-opcao__prazo">${prazo}</p>` : ''}
          </div>
          <span class="mare-frete-opcao__valor">${exibir}</span>
        </div>
      `;
    }).join('')}
  `;
}

/* ─── Calcular frete ─── */
window.mareCalcularFrete = async function (triggerBtn) {
  const widget = triggerBtn
    ? triggerBtn.closest('[data-frete-widget]')
    : document.querySelector('[data-frete-widget]');
  if (!widget) return;

  const input  = widget.querySelector('[data-frete-cep]');
  const btn    = widget.querySelector('[data-frete-btn]');
  const result = widget.querySelector('[data-frete-result]');
  const cep    = input.value.replace(/\D/g, '');

  if (cep.length !== 8) {
    result.innerHTML = '<p class="mare-frete-msg erro">CEP inválido. Informe 8 dígitos.</p>';
    return;
  }

  btn.disabled    = true;
  btn.textContent = 'Calculando...';
  result.innerHTML = '';

  try {
    const viaCep = await _buscarCep(cep);
    if (viaCep.erro) {
      result.innerHTML = '<p class="mare-frete-msg erro">CEP não encontrado. Verifique e tente novamente.</p>';
      return;
    }

    _atualizarProgressFrete(viaCep.uf);
    localStorage.setItem('mare_cep_uf', viaCep.uf);

    /* ── 1. Tenta tarifas reais do Shopify/Frenet ── */
    let servicos = [];
    try {
      const cart0 = await fetch('/cart.js').then(r => r.json());
      servicos = await _buscarTarifasShopify(cep, viaCep.uf, viaCep.localidade, cart0.total_price);
    } catch (_) { /* ignora — cai no fallback */ }

    if (servicos.length > 0) {
      _renderizarServicos(servicos, viaCep, 'shopify', widget);
      localStorage.setItem('mare_cep', cep);
      return;
    }

    /* ── 2. Fallback: tabela Correios local ── */
    const cart = await fetch('/cart.js').then(r => r.json());

    const totalItens = cart.items.reduce((s, i) => s + i.quantity, 0);
    const pesoRealG  = cart.items.reduce((s, i) => {
      return s + (i.grams > 0 ? i.grams : PESO_PADRAO_G) * i.quantity;
    }, 0);
    const pesoFinalG = Math.max(pesoRealG, pesoCubico(totalItens));
    const regiao     = getRegiao(viaCep.uf);

    const freteGratis = cart.total_price >= _freteGratisMin(viaCep.uf);
    const menorLocal  = Math.min(
      ...['PAC','SEDEX'].map(s => calcularValorLocal(s, pesoFinalG, regiao) ?? Infinity)
    );

    const servicosLocal = ['PAC', 'SEDEX'].map(s => {
      const valor = calcularValorLocal(s, pesoFinalG, regiao);
      if (valor === null) return null;
      const isCheapest = valor === menorLocal;
      return {
        nome:       s,
        price:      freteGratis && isCheapest ? 0 : valor,
        prazoLabel: `até ${(s === 'PAC' ? _prazoRegional(viaCep.uf) : (_prazoFallback(s) ?? _prazoRegional(viaCep.uf))) + 2} dias úteis`,
      };
    }).filter(Boolean);

    if (!servicosLocal.length) {
      result.innerHTML = '<p class="mare-frete-msg">Peso acima do limite. Entre em contato para cotação.</p>';
      return;
    }

    _renderizarServicos(servicosLocal, viaCep, 'local', widget);
    localStorage.removeItem('mare_cep_limpo');
    localStorage.setItem('mare_cep', cep);

  } catch (err) {
    result.innerHTML = '<p class="mare-frete-msg erro">Erro ao calcular. Tente novamente.</p>';
    console.error('[Maré Frete]', err);
  } finally {
    btn.disabled    = false;
    btn.textContent = 'Calcular';
  }
};

/* ─── Selecionar opção de frete ─── */
window.mareSelecionarFrete = function (el) {
  document.querySelectorAll('.mare-frete-opcao').forEach(o => o.classList.remove('selecionado'));
  el.classList.add('selecionado');
};

/* ─── Atualizar barra de progresso de frete grátis por região ─── */
function _atualizarProgressFrete(uf) {
  const meta = _freteGratisMin(uf);
  fetch('/cart.js').then(r => r.json()).then(cart => {
    const total = cart.total_price;
    const falta = meta - total;
    const pct   = Math.min(100, Math.floor(total * 100 / meta));

    document.querySelectorAll('[data-progress-frete-label]').forEach(el => {
      if (falta <= 0) {
        el.innerHTML = '<span class="mare-progress__label mare-progress__label--ok">Você ganhou frete grátis!</span>';
      } else {
        const reais = (falta / 100).toFixed(2).replace('.', ',');
        el.innerHTML = `<span class="mare-progress__label">Falta <strong>R$ ${reais}</strong> para frete grátis</span>`;
      }
    });

    document.querySelectorAll('[data-progress-frete-fill]').forEach(el => {
      el.style.width = pct + '%';
    });
  }).catch(() => {});
}

/* ─── Restaurar CEP salvo ─── */
function _restaurarCep() {
  if (_cepEmFoco) return;
  const cepSalvo = localStorage.getItem('mare_cep');
  if (!cepSalvo) return;
  document.querySelectorAll('[data-frete-cep]').forEach(input => {
    if (input.value.replace(/\D/g, '') === cepSalvo) return;
    input.value = cepSalvo.length > 5
      ? cepSalvo.slice(0, 5) + '-' + cepSalvo.slice(5, 8)
      : cepSalvo;
  });
  const ufSalva = localStorage.getItem('mare_cep_uf');
  if (ufSalva) _atualizarProgressFrete(ufSalva);
}

/* ─── Observar re-renderização do carrinho ─── */
function _observarCarrinho() {
  const cartEl = document.querySelector('cart-items-component');
  if (!cartEl) return;
  new MutationObserver(_restaurarCep).observe(cartEl, { childList: true, subtree: true });
}

document.addEventListener('DOMContentLoaded', () => {
  _restaurarCep();
  _observarCarrinho();
});

/* ─── Cross sell: adicionar ao carrinho ─── */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.mare-cs-card__btn');
  if (!btn || btn.disabled) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  btn.disabled    = true;
  btn.textContent = 'Adicionando...';

  try {
    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });

    if (!res.ok) throw new Error('Erro');

    btn.textContent = 'Adicionado!';
    btn.classList.add('adicionado');

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    setTimeout(() => {
      const sectionId = document.querySelector('cart-items-component')?.dataset.sectionId;
      if (!sectionId) return;
      fetch(`/?sections=${sectionId}`)
        .then(r => r.json())
        .then(data => {
          const html  = data[sectionId];
          if (!html) return;
          const doc   = new DOMParser().parseFromString(html, 'text/html');
          const novo  = doc.querySelector('cart-items-component');
          const atual = document.querySelector('cart-items-component');
          if (novo && atual) atual.innerHTML = novo.innerHTML;
        })
        .catch(() => {});
    }, 500);

  } catch {
    btn.textContent = 'Erro — tente novamente';
    btn.disabled    = false;
  }
});
